export type YourImaiCopy = {
  /** Sidebar row label and page title. */
  navLabel: string;
  greeting: string;
  greetingNamed: string;
  greetingBody: string;
  greetingAskName: string;
  customize: string;
  customizeNamed: string;
  colors: string;
  shapes: string;
  faces: string;
  name: string;
  save: string;
  close: string;
  previous: string;
  next: string;
  saveFailed: string;
  thinking: string;
  readAt: string;
  opening: string;
  openFailed: string;
  retry: string;
  /** Composer placeholder; `{name}` is IMAI's name. */
  placeholder: string;
  send: string;
  delivered: string;
  read: string;
  notDelivered: string;
  retrySend: string;
  typing: string;
  replyFailed: string;
  conversation: string;
  loadEarlier: string;
  unread: string;
  you: string;
  imaiName: string;
  threadWorking: string;
  threadNeedsYou: string;
  threadStopped: string;
  threadDidntFinish: string;
  threadDone: string;
  openThread: string;
  /** `{name}` is IMAI's name; badge on a message IMAI sent into a thread. */
  messageFrom: string;
  /** `{title}` is a task's title; above its question shown in this chat. */
  questionFrom: string;
  /** Under a task's card, beside the images forwarded to it. */
  sentToTask: string;
  /** The text a failed task's Retry sends IMAI; `{title}` is the task. */
  retryTaskPrompt: string;
  react: string;
  reply: string;
  reactionLove: string;
  reactionLike: string;
  reactionDislike: string;
  reactionLaugh: string;
  reactionWow: string;
  reactionSad: string;
  /** `{reaction}` is a reaction label. */
  youReacted: string;
  imaiReacted: string;
  /** `{name}` is IMAI's name. */
  replyingTo: string;
  replyingToSelf: string;
  cancelReply: string;
  /** `{text}` is the quoted text. */
  showQuoted: string;
  photo: string;
  photoUnavailable: string;
  newMessage: string;
  notifyPrompt: string;
  notifyTurnOn: string;
  notifyDismiss: string;
  latestUpdate: string;
  glimpseEmpty: string;
  goToChat: string;
  /** The group of files a finished thread produced, under IMAI's reply. */
  outputsLabel: string;
  stickerGenerating: string;
  /** `{label}` is the sticker's short description. */
  stickerLabel: string;
};

const DEFAULT_COPY: YourImaiCopy = {
  navLabel: 'Your IMAI',
  greeting: 'Hey! I’m your IMAI.',
  greetingNamed: 'Hey! I’m {name}.',
  greetingBody:
    'Text me anytime. I’ll keep things moving, even when we’re not talking, and check in with updates or questions.',
  greetingAskName: 'Want to give me a name?',
  customize: 'Customize your IMAI',
  customizeNamed: 'Customize {name}',
  colors: 'Colors',
  shapes: 'Shapes',
  faces: 'Faces',
  name: 'Name',
  save: 'Save',
  close: 'Close',
  previous: 'Previous',
  next: 'Next',
  saveFailed: 'Couldn’t save. Try again.',
  thinking: 'Thinking',
  readAt: 'Read {time}',
  opening: 'Opening your chat…',
  openFailed: 'Couldn’t open Your IMAI.',
  retry: 'Try again',
  placeholder: 'Text {name}',
  send: 'Send',
  delivered: 'Delivered',
  read: 'Read',
  notDelivered: 'Not delivered',
  retrySend: 'Retry',
  typing: 'IMAI is typing',
  replyFailed: 'IMAI couldn’t reply. Send another text to try again.',
  conversation: 'Conversation with IMAI',
  loadEarlier: 'Load earlier messages',
  unread: '{count} new',
  you: 'You',
  imaiName: 'IMAI',
  threadWorking: 'Working',
  threadNeedsYou: 'Needs you',
  threadStopped: 'Stopped',
  threadDidntFinish: "Didn't finish",
  threadDone: 'Done',
  openThread: 'Open {title}',
  messageFrom: 'Message from {name}',
  questionFrom: 'From {title}',
  sentToTask: 'Sent to the task',
  retryTaskPrompt: 'Try “{title}” again',
  react: 'React',
  reply: 'Reply',
  reactionLove: 'Love',
  reactionLike: 'Like',
  reactionDislike: 'Dislike',
  reactionLaugh: 'Haha',
  reactionWow: 'Wow',
  reactionSad: 'Sad',
  youReacted: 'You reacted {reaction}',
  imaiReacted: 'IMAI reacted {reaction}',
  replyingTo: 'Replying to {name}',
  replyingToSelf: 'Replying to yourself',
  cancelReply: 'Cancel reply',
  showQuoted: 'Show quoted message: {text}',
  photo: 'Photo',
  photoUnavailable: 'Image unavailable',
  newMessage: 'New message',
  notifyPrompt: 'Get notified when {name} texts you',
  notifyTurnOn: 'Turn on',
  notifyDismiss: 'Not now',
  latestUpdate: 'Latest update',
  glimpseEmpty: 'Nothing to show yet.',
  goToChat: 'Go to chat',
  outputsLabel: 'Files from finished work',
  stickerGenerating: 'Making a sticker',
  stickerLabel: 'Sticker: {label}',
};

const COPY_BY_LANGUAGE: Record<string, YourImaiCopy> = {
  ar: {
    navLabel: 'IMAI الخاص بك',
    greeting: 'مرحبًا! أنا IMAI الخاص بك.',
    greetingNamed: 'مرحبًا! أنا {name}.',
    greetingBody:
      'راسلني في أي وقت. سأواصل إنجاز الأمور حتى عندما لا نتحدث، وسأطلعك على المستجدات أو أسألك عند الحاجة.',
    greetingAskName: 'هل تريد أن تمنحني اسمًا؟',
    customize: 'خصّص IMAI الخاص بك',
    customizeNamed: 'خصّص {name}',
    colors: 'الألوان',
    shapes: 'الأشكال',
    faces: 'التعابير',
    name: 'الاسم',
    save: 'حفظ',
    close: 'إغلاق',
    previous: 'السابق',
    next: 'التالي',
    saveFailed: 'تعذّر الحفظ. حاول مرة أخرى.',
    thinking: 'يفكّر',
    readAt: 'تمت القراءة {time}',
    opening: 'جارٍ فتح محادثتك…',
    openFailed: 'تعذّر فتح IMAI الخاص بك.',
    retry: 'حاول مرة أخرى',
    placeholder: 'راسل {name}',
    send: 'إرسال',
    delivered: 'تم التسليم',
    read: 'تمت القراءة',
    notDelivered: 'لم يتم التسليم',
    retrySend: 'إعادة المحاولة',
    typing: 'IMAI يكتب',
    replyFailed: 'تعذّر على IMAI الرد. أرسل رسالة أخرى للمحاولة مجددًا.',
    conversation: 'محادثة مع IMAI',
    loadEarlier: 'تحميل الرسائل السابقة',
    unread: '{count} جديدة',
    you: 'أنت',
    imaiName: 'IMAI',
    threadWorking: 'قيد العمل',
    threadNeedsYou: 'يحتاج إليك',
    threadStopped: 'توقف',
    threadDidntFinish: 'لم يكتمل',
    threadDone: 'تم',
    openThread: 'افتح {title}',
    messageFrom: 'رسالة من {name}',
    questionFrom: 'من {title}',
    sentToTask: 'أُرسلت إلى المهمة',
    retryTaskPrompt: 'أعد محاولة «{title}»',
    react: 'تفاعل',
    reply: 'رد',
    reactionLove: 'أحببته',
    reactionLike: 'إعجاب',
    reactionDislike: 'عدم إعجاب',
    reactionLaugh: 'هاها',
    reactionWow: 'واو',
    reactionSad: 'حزين',
    youReacted: 'تفاعلت بـ {reaction}',
    imaiReacted: 'تفاعل IMAI بـ {reaction}',
    replyingTo: 'الرد على {name}',
    replyingToSelf: 'الرد على نفسك',
    cancelReply: 'إلغاء الرد',
    showQuoted: 'عرض الرسالة المقتبسة: {text}',
    photo: 'صورة',
    photoUnavailable: 'الصورة غير متاحة',
    newMessage: 'رسالة جديدة',
    notifyPrompt: 'احصل على إشعار عندما يراسلك {name}',
    notifyTurnOn: 'تفعيل',
    notifyDismiss: 'ليس الآن',
    latestUpdate: 'آخر تحديث',
    glimpseEmpty: 'لا يوجد شيء لعرضه بعد.',
    goToChat: 'الانتقال إلى المحادثة',
    outputsLabel: 'ملفات من العمل المكتمل',
    stickerGenerating: 'جارٍ إنشاء ملصق',
    stickerLabel: 'ملصق: {label}',
  },
  de: {
    navLabel: 'Dein IMAI',
    greeting: 'Hey! Ich bin dein IMAI.',
    greetingNamed: 'Hey! Ich bin {name}.',
    greetingBody:
      'Schreib mir jederzeit. Ich bleibe dran, auch wenn wir gerade nicht schreiben, und melde mich mit Updates oder Fragen.',
    greetingAskName: 'Willst du mir einen Namen geben?',
    customize: 'Dein IMAI anpassen',
    customizeNamed: '{name} anpassen',
    colors: 'Farben',
    shapes: 'Formen',
    faces: 'Gesichter',
    name: 'Name',
    save: 'Speichern',
    close: 'Schließen',
    previous: 'Zurück',
    next: 'Weiter',
    saveFailed: 'Speichern fehlgeschlagen. Versuch es noch einmal.',
    thinking: 'Denkt nach',
    readAt: 'Gelesen {time}',
    opening: 'Dein Chat wird geöffnet…',
    openFailed: 'Dein IMAI konnte nicht geöffnet werden.',
    retry: 'Erneut versuchen',
    placeholder: 'Nachricht an {name}',
    send: 'Senden',
    delivered: 'Zugestellt',
    read: 'Gelesen',
    notDelivered: 'Nicht zugestellt',
    retrySend: 'Erneut senden',
    typing: 'IMAI schreibt',
    replyFailed:
      'IMAI konnte nicht antworten. Schick eine weitere Nachricht, um es erneut zu versuchen.',
    conversation: 'Unterhaltung mit IMAI',
    loadEarlier: 'Frühere Nachrichten laden',
    unread: '{count} neu',
    you: 'Du',
    imaiName: 'IMAI',
    threadWorking: 'In Arbeit',
    threadNeedsYou: 'Braucht dich',
    threadStopped: 'Gestoppt',
    threadDidntFinish: 'Nicht fertig',
    threadDone: 'Fertig',
    openThread: '{title} öffnen',
    messageFrom: 'Nachricht von {name}',
    questionFrom: 'Von {title}',
    sentToTask: 'An die Aufgabe gesendet',
    retryTaskPrompt: 'Versuche „{title}“ noch einmal',
    react: 'Reagieren',
    reply: 'Antworten',
    reactionLove: 'Liebe',
    reactionLike: 'Gefällt mir',
    reactionDislike: 'Gefällt mir nicht',
    reactionLaugh: 'Haha',
    reactionWow: 'Wow',
    reactionSad: 'Traurig',
    youReacted: 'Du hast mit {reaction} reagiert',
    imaiReacted: 'IMAI hat mit {reaction} reagiert',
    replyingTo: 'Antwort an {name}',
    replyingToSelf: 'Antwort an dich selbst',
    cancelReply: 'Antwort abbrechen',
    showQuoted: 'Zitierte Nachricht anzeigen: {text}',
    photo: 'Foto',
    photoUnavailable: 'Bild nicht verfügbar',
    newMessage: 'Neue Nachricht',
    notifyPrompt: 'Lass dich benachrichtigen, wenn {name} dir schreibt',
    notifyTurnOn: 'Aktivieren',
    notifyDismiss: 'Nicht jetzt',
    latestUpdate: 'Letztes Update',
    glimpseEmpty: 'Noch nichts zu sehen.',
    goToChat: 'Zum Chat',
    outputsLabel: 'Dateien aus abgeschlossener Arbeit',
    stickerGenerating: 'Sticker wird erstellt',
    stickerLabel: 'Sticker: {label}',
  },
  es: {
    navLabel: 'Tu IMAI',
    greeting: '¡Hola! Soy tu IMAI.',
    greetingNamed: '¡Hola! Soy {name}.',
    greetingBody:
      'Escríbeme cuando quieras. Seguiré avanzando aunque no estemos hablando y te escribiré con novedades o preguntas.',
    greetingAskName: '¿Quieres ponerme un nombre?',
    customize: 'Personaliza tu IMAI',
    customizeNamed: 'Personaliza a {name}',
    colors: 'Colores',
    shapes: 'Formas',
    faces: 'Caras',
    name: 'Nombre',
    save: 'Guardar',
    close: 'Cerrar',
    previous: 'Anterior',
    next: 'Siguiente',
    saveFailed: 'No se pudo guardar. Inténtalo de nuevo.',
    thinking: 'Pensando',
    readAt: 'Leído {time}',
    opening: 'Abriendo tu chat…',
    openFailed: 'No se pudo abrir Tu IMAI.',
    retry: 'Reintentar',
    placeholder: 'Escribe a {name}',
    send: 'Enviar',
    delivered: 'Entregado',
    read: 'Leído',
    notDelivered: 'No entregado',
    retrySend: 'Reintentar',
    typing: 'IMAI está escribiendo',
    replyFailed:
      'IMAI no pudo responder. Envía otro mensaje para intentarlo de nuevo.',
    conversation: 'Conversación con IMAI',
    loadEarlier: 'Cargar mensajes anteriores',
    unread: '{count} nuevos',
    you: 'Tú',
    imaiName: 'IMAI',
    threadWorking: 'Trabajando',
    threadNeedsYou: 'Te necesita',
    threadStopped: 'Detenido',
    threadDidntFinish: 'No terminó',
    threadDone: 'Listo',
    openThread: 'Abrir {title}',
    messageFrom: 'Mensaje de {name}',
    questionFrom: 'De {title}',
    sentToTask: 'Enviado a la tarea',
    retryTaskPrompt: 'Vuelve a intentar «{title}»',
    react: 'Reaccionar',
    reply: 'Responder',
    reactionLove: 'Me encanta',
    reactionLike: 'Me gusta',
    reactionDislike: 'No me gusta',
    reactionLaugh: 'Jaja',
    reactionWow: 'Guau',
    reactionSad: 'Triste',
    youReacted: 'Reaccionaste con {reaction}',
    imaiReacted: 'IMAI reaccionó con {reaction}',
    replyingTo: 'Respondiendo a {name}',
    replyingToSelf: 'Respondiéndote a ti',
    cancelReply: 'Cancelar respuesta',
    showQuoted: 'Mostrar mensaje citado: {text}',
    photo: 'Foto',
    photoUnavailable: 'Imagen no disponible',
    newMessage: 'Mensaje nuevo',
    notifyPrompt: 'Recibe un aviso cuando {name} te escriba',
    notifyTurnOn: 'Activar',
    notifyDismiss: 'Ahora no',
    latestUpdate: 'Última actualización',
    glimpseEmpty: 'Aún no hay nada que mostrar.',
    goToChat: 'Ir al chat',
    outputsLabel: 'Archivos del trabajo terminado',
    stickerGenerating: 'Creando un sticker',
    stickerLabel: 'Sticker: {label}',
  },
  fr: {
    navLabel: 'Votre IMAI',
    greeting: 'Salut ! Je suis ton IMAI.',
    greetingNamed: 'Salut ! Je suis {name}.',
    greetingBody:
      'Écris-moi quand tu veux. Je fais avancer les choses même quand on ne parle pas, et je reviens vers toi avec des nouvelles ou des questions.',
    greetingAskName: 'Tu veux me donner un nom ?',
    customize: 'Personnaliser ton IMAI',
    customizeNamed: 'Personnaliser {name}',
    colors: 'Couleurs',
    shapes: 'Formes',
    faces: 'Visages',
    name: 'Nom',
    save: 'Enregistrer',
    close: 'Fermer',
    previous: 'Précédent',
    next: 'Suivant',
    saveFailed: 'Échec de l’enregistrement. Réessaie.',
    thinking: 'Réfléchit',
    readAt: 'Lu {time}',
    opening: 'Ouverture de votre discussion…',
    openFailed: 'Impossible d’ouvrir Votre IMAI.',
    retry: 'Réessayer',
    placeholder: 'Écrire à {name}',
    send: 'Envoyer',
    delivered: 'Distribué',
    read: 'Lu',
    notDelivered: 'Non distribué',
    retrySend: 'Réessayer',
    typing: 'IMAI écrit',
    replyFailed:
      'IMAI n’a pas pu répondre. Envoyez un autre message pour réessayer.',
    conversation: 'Conversation avec IMAI',
    loadEarlier: 'Charger les messages précédents',
    unread: '{count} nouveaux',
    you: 'Vous',
    imaiName: 'IMAI',
    threadWorking: 'En cours',
    threadNeedsYou: 'A besoin de toi',
    threadStopped: 'Arrêté',
    threadDidntFinish: 'Inachevé',
    threadDone: 'Terminé',
    openThread: 'Ouvrir {title}',
    messageFrom: 'Message de {name}',
    questionFrom: 'De {title}',
    sentToTask: 'Envoyé à la tâche',
    retryTaskPrompt: 'Réessaie « {title} »',
    react: 'Réagir',
    reply: 'Répondre',
    reactionLove: 'J’adore',
    reactionLike: 'J’aime',
    reactionDislike: 'Je n’aime pas',
    reactionLaugh: 'Haha',
    reactionWow: 'Waouh',
    reactionSad: 'Triste',
    youReacted: 'Vous avez réagi avec {reaction}',
    imaiReacted: 'IMAI a réagi avec {reaction}',
    replyingTo: 'Réponse à {name}',
    replyingToSelf: 'Réponse à vous-même',
    cancelReply: 'Annuler la réponse',
    showQuoted: 'Afficher le message cité : {text}',
    photo: 'Photo',
    photoUnavailable: 'Image indisponible',
    newMessage: 'Nouveau message',
    notifyPrompt: 'Recevez une notification quand {name} vous écrit',
    notifyTurnOn: 'Activer',
    notifyDismiss: 'Plus tard',
    latestUpdate: 'Dernière mise à jour',
    glimpseEmpty: 'Rien à afficher pour l’instant.',
    goToChat: 'Aller à la discussion',
    outputsLabel: 'Fichiers du travail terminé',
    stickerGenerating: 'Création d’un sticker',
    stickerLabel: 'Sticker : {label}',
  },
  hi: {
    navLabel: 'आपका IMAI',
    greeting: 'नमस्ते! मैं आपका IMAI हूँ।',
    greetingNamed: 'नमस्ते! मैं {name} हूँ।',
    greetingBody:
      'मुझे कभी भी मैसेज करें। हम बात न भी कर रहे हों, तब भी मैं काम आगे बढ़ाता रहूँगा और अपडेट या सवालों के साथ आपसे संपर्क करूँगा।',
    greetingAskName: 'क्या आप मुझे कोई नाम देना चाहेंगे?',
    customize: 'अपना IMAI कस्टमाइज़ करें',
    customizeNamed: '{name} को कस्टमाइज़ करें',
    colors: 'रंग',
    shapes: 'आकार',
    faces: 'चेहरे',
    name: 'नाम',
    save: 'सेव करें',
    close: 'बंद करें',
    previous: 'पिछला',
    next: 'अगला',
    saveFailed: 'सेव नहीं हो सका। फिर से कोशिश करें।',
    thinking: 'सोच रहा है',
    readAt: 'पढ़ा गया {time}',
    opening: 'आपकी चैट खुल रही है…',
    openFailed: 'आपका IMAI नहीं खुल सका।',
    retry: 'फिर से कोशिश करें',
    placeholder: '{name} को लिखें',
    send: 'भेजें',
    delivered: 'पहुँच गया',
    read: 'पढ़ लिया',
    notDelivered: 'नहीं पहुँचा',
    retrySend: 'फिर से भेजें',
    typing: 'IMAI लिख रहा है',
    replyFailed:
      'IMAI जवाब नहीं दे सका। फिर से कोशिश करने के लिए एक और संदेश भेजें।',
    conversation: 'IMAI के साथ बातचीत',
    loadEarlier: 'पिछले संदेश लोड करें',
    unread: '{count} नए',
    you: 'आप',
    imaiName: 'IMAI',
    threadWorking: 'काम चल रहा है',
    threadNeedsYou: 'आपकी ज़रूरत है',
    threadStopped: 'रुका हुआ',
    threadDidntFinish: 'पूरा नहीं हुआ',
    threadDone: 'पूरा',
    openThread: '{title} खोलें',
    messageFrom: '{name} का संदेश',
    questionFrom: '{title} से',
    sentToTask: 'कार्य को भेजा गया',
    retryTaskPrompt: '“{title}” फिर से आज़माएँ',
    react: 'प्रतिक्रिया दें',
    reply: 'जवाब दें',
    reactionLove: 'प्यार',
    reactionLike: 'पसंद',
    reactionDislike: 'नापसंद',
    reactionLaugh: 'हाहा',
    reactionWow: 'वाह',
    reactionSad: 'उदास',
    youReacted: 'आपने {reaction} से प्रतिक्रिया दी',
    imaiReacted: 'IMAI ने {reaction} से प्रतिक्रिया दी',
    replyingTo: '{name} को जवाब',
    replyingToSelf: 'खुद को जवाब',
    cancelReply: 'जवाब रद्द करें',
    showQuoted: 'उद्धृत संदेश दिखाएँ: {text}',
    photo: 'फ़ोटो',
    photoUnavailable: 'छवि उपलब्ध नहीं है',
    newMessage: 'नया संदेश',
    notifyPrompt: '{name} का मैसेज आने पर सूचना पाएँ',
    notifyTurnOn: 'चालू करें',
    notifyDismiss: 'अभी नहीं',
    latestUpdate: 'नया अपडेट',
    glimpseEmpty: 'अभी दिखाने के लिए कुछ नहीं है।',
    goToChat: 'चैट पर जाएँ',
    outputsLabel: 'पूरे हुए काम की फ़ाइलें',
    stickerGenerating: 'स्टिकर बन रहा है',
    stickerLabel: 'स्टिकर: {label}',
  },
  id: {
    navLabel: 'IMAI Anda',
    greeting: 'Hai! Aku IMAI-mu.',
    greetingNamed: 'Hai! Aku {name}.',
    greetingBody:
      'Kirim pesan kapan saja. Aku akan terus menjalankan pekerjaan meski kita sedang tidak mengobrol, dan mengabarimu dengan info terbaru atau pertanyaan.',
    greetingAskName: 'Mau memberiku nama?',
    customize: 'Sesuaikan IMAI-mu',
    customizeNamed: 'Sesuaikan {name}',
    colors: 'Warna',
    shapes: 'Bentuk',
    faces: 'Wajah',
    name: 'Nama',
    save: 'Simpan',
    close: 'Tutup',
    previous: 'Sebelumnya',
    next: 'Berikutnya',
    saveFailed: 'Gagal menyimpan. Coba lagi.',
    thinking: 'Berpikir',
    readAt: 'Dibaca {time}',
    opening: 'Membuka obrolan Anda…',
    openFailed: 'IMAI Anda tidak dapat dibuka.',
    retry: 'Coba lagi',
    placeholder: 'Kirim pesan ke {name}',
    send: 'Kirim',
    delivered: 'Terkirim',
    read: 'Dibaca',
    notDelivered: 'Tidak terkirim',
    retrySend: 'Coba lagi',
    typing: 'IMAI sedang mengetik',
    replyFailed:
      'IMAI tidak dapat membalas. Kirim pesan lain untuk mencoba lagi.',
    conversation: 'Percakapan dengan IMAI',
    loadEarlier: 'Muat pesan sebelumnya',
    unread: '{count} baru',
    you: 'Anda',
    imaiName: 'IMAI',
    threadWorking: 'Sedang dikerjakan',
    threadNeedsYou: 'Butuh kamu',
    threadStopped: 'Berhenti',
    threadDidntFinish: 'Tidak selesai',
    threadDone: 'Selesai',
    openThread: 'Buka {title}',
    messageFrom: 'Pesan dari {name}',
    questionFrom: 'Dari {title}',
    sentToTask: 'Dikirim ke tugas',
    retryTaskPrompt: 'Coba lagi “{title}”',
    react: 'Beri reaksi',
    reply: 'Balas',
    reactionLove: 'Suka sekali',
    reactionLike: 'Suka',
    reactionDislike: 'Tidak suka',
    reactionLaugh: 'Haha',
    reactionWow: 'Wow',
    reactionSad: 'Sedih',
    youReacted: 'Anda bereaksi {reaction}',
    imaiReacted: 'IMAI bereaksi {reaction}',
    replyingTo: 'Membalas {name}',
    replyingToSelf: 'Membalas diri sendiri',
    cancelReply: 'Batalkan balasan',
    showQuoted: 'Tampilkan pesan yang dikutip: {text}',
    photo: 'Foto',
    photoUnavailable: 'Gambar tidak tersedia',
    newMessage: 'Pesan baru',
    notifyPrompt: 'Dapatkan notifikasi saat {name} mengirim pesan',
    notifyTurnOn: 'Aktifkan',
    notifyDismiss: 'Nanti saja',
    latestUpdate: 'Info terbaru',
    glimpseEmpty: 'Belum ada yang bisa ditampilkan.',
    goToChat: 'Buka obrolan',
    outputsLabel: 'File dari pekerjaan yang selesai',
    stickerGenerating: 'Membuat stiker',
    stickerLabel: 'Stiker: {label}',
  },
  it: {
    navLabel: 'Il tuo IMAI',
    greeting: 'Ciao! Sono il tuo IMAI.',
    greetingNamed: 'Ciao! Sono {name}.',
    greetingBody:
      'Scrivimi quando vuoi. Porto avanti il lavoro anche quando non stiamo parlando e ti aggiorno o ti faccio domande.',
    greetingAskName: 'Vuoi darmi un nome?',
    customize: 'Personalizza il tuo IMAI',
    customizeNamed: 'Personalizza {name}',
    colors: 'Colori',
    shapes: 'Forme',
    faces: 'Facce',
    name: 'Nome',
    save: 'Salva',
    close: 'Chiudi',
    previous: 'Precedente',
    next: 'Successivo',
    saveFailed: 'Impossibile salvare. Riprova.',
    thinking: 'Sta pensando',
    readAt: 'Letto {time}',
    opening: 'Apertura della tua chat…',
    openFailed: 'Impossibile aprire Il tuo IMAI.',
    retry: 'Riprova',
    placeholder: 'Scrivi a {name}',
    send: 'Invia',
    delivered: 'Consegnato',
    read: 'Letto',
    notDelivered: 'Non consegnato',
    retrySend: 'Riprova',
    typing: 'IMAI sta scrivendo',
    replyFailed:
      'IMAI non è riuscito a rispondere. Invia un altro messaggio per riprovare.',
    conversation: 'Conversazione con IMAI',
    loadEarlier: 'Carica messaggi precedenti',
    unread: '{count} nuovi',
    you: 'Tu',
    imaiName: 'IMAI',
    threadWorking: 'In corso',
    threadNeedsYou: 'Ha bisogno di te',
    threadStopped: 'Interrotto',
    threadDidntFinish: 'Non completato',
    threadDone: 'Fatto',
    openThread: 'Apri {title}',
    messageFrom: 'Messaggio da {name}',
    questionFrom: 'Da {title}',
    sentToTask: 'Inviato all’attività',
    retryTaskPrompt: 'Riprova “{title}”',
    react: 'Reagisci',
    reply: 'Rispondi',
    reactionLove: 'Adoro',
    reactionLike: 'Mi piace',
    reactionDislike: 'Non mi piace',
    reactionLaugh: 'Ahah',
    reactionWow: 'Wow',
    reactionSad: 'Triste',
    youReacted: 'Hai reagito con {reaction}',
    imaiReacted: 'IMAI ha reagito con {reaction}',
    replyingTo: 'Risposta a {name}',
    replyingToSelf: 'Risposta a te stesso',
    cancelReply: 'Annulla risposta',
    showQuoted: 'Mostra messaggio citato: {text}',
    photo: 'Foto',
    photoUnavailable: 'Immagine non disponibile',
    newMessage: 'Nuovo messaggio',
    notifyPrompt: 'Ricevi una notifica quando {name} ti scrive',
    notifyTurnOn: 'Attiva',
    notifyDismiss: 'Non ora',
    latestUpdate: 'Ultimo aggiornamento',
    glimpseEmpty: 'Ancora niente da mostrare.',
    goToChat: 'Vai alla chat',
    outputsLabel: 'File del lavoro completato',
    stickerGenerating: 'Creazione dello sticker',
    stickerLabel: 'Sticker: {label}',
  },
  ja: {
    navLabel: 'あなたのIMAI',
    greeting: 'こんにちは！あなたの IMAI です。',
    greetingNamed: 'こんにちは！{name} です。',
    greetingBody:
      'いつでもメッセージしてください。話していない間も作業を進め、進捗や質問があれば連絡します。',
    greetingAskName: '名前をつけてくれますか？',
    customize: 'IMAI をカスタマイズ',
    customizeNamed: '{name} をカスタマイズ',
    colors: 'カラー',
    shapes: '形',
    faces: '表情',
    name: '名前',
    save: '保存',
    close: '閉じる',
    previous: '前へ',
    next: '次へ',
    saveFailed: '保存できませんでした。もう一度お試しください。',
    thinking: '考え中',
    readAt: '既読 {time}',
    opening: 'チャットを開いています…',
    openFailed: 'あなたのIMAIを開けませんでした。',
    retry: '再試行',
    placeholder: '{name}にメッセージ',
    send: '送信',
    delivered: '配信済み',
    read: '既読',
    notDelivered: '未配信',
    retrySend: '再送信',
    typing: 'IMAIが入力中',
    replyFailed:
      'IMAIが返信できませんでした。もう一度メッセージを送ってください。',
    conversation: 'IMAIとの会話',
    loadEarlier: '以前のメッセージを読み込む',
    unread: '新着 {count} 件',
    you: 'あなた',
    imaiName: 'IMAI',
    threadWorking: '作業中',
    threadNeedsYou: 'あなたの回答待ち',
    threadStopped: '停止',
    threadDidntFinish: '完了できず',
    threadDone: '完了',
    openThread: '{title} を開く',
    messageFrom: '{name} からのメッセージ',
    questionFrom: '{title} から',
    sentToTask: 'タスクに送信済み',
    retryTaskPrompt: '「{title}」をもう一度試して',
    react: 'リアクション',
    reply: '返信',
    reactionLove: '大好き',
    reactionLike: 'いいね',
    reactionDislike: 'よくないね',
    reactionLaugh: 'ハハ',
    reactionWow: 'すごい',
    reactionSad: '悲しい',
    youReacted: '{reaction} でリアクションしました',
    imaiReacted: 'IMAI が {reaction} でリアクションしました',
    replyingTo: '{name} に返信',
    replyingToSelf: '自分に返信',
    cancelReply: '返信をキャンセル',
    showQuoted: '引用メッセージを表示: {text}',
    photo: '写真',
    photoUnavailable: '画像を表示できません',
    newMessage: '新着メッセージ',
    notifyPrompt: '{name} からメッセージが届いたら通知を受け取る',
    notifyTurnOn: 'オンにする',
    notifyDismiss: '後で',
    latestUpdate: '最新の進捗',
    glimpseEmpty: 'まだ表示するものはありません。',
    goToChat: 'チャットへ移動',
    outputsLabel: '完了した作業のファイル',
    stickerGenerating: 'ステッカーを作成中',
    stickerLabel: 'ステッカー: {label}',
  },
  ko: {
    navLabel: '나의 IMAI',
    greeting: '안녕하세요! 당신의 IMAI예요.',
    greetingNamed: '안녕하세요! 저는 {name}예요.',
    greetingBody:
      '언제든 메시지 주세요. 대화하지 않을 때도 일을 계속 진행하고, 업데이트나 질문이 있으면 연락드릴게요.',
    greetingAskName: '제게 이름을 지어 주실래요?',
    customize: 'IMAI 맞춤 설정',
    customizeNamed: '{name} 맞춤 설정',
    colors: '색상',
    shapes: '모양',
    faces: '표정',
    name: '이름',
    save: '저장',
    close: '닫기',
    previous: '이전',
    next: '다음',
    saveFailed: '저장하지 못했어요. 다시 시도해 주세요.',
    thinking: '생각 중',
    readAt: '읽음 {time}',
    opening: '채팅을 여는 중…',
    openFailed: '나의 IMAI를 열 수 없습니다.',
    retry: '다시 시도',
    placeholder: '{name}에게 메시지 보내기',
    send: '보내기',
    delivered: '전송됨',
    read: '읽음',
    notDelivered: '전송되지 않음',
    retrySend: '다시 보내기',
    typing: 'IMAI가 입력 중',
    replyFailed:
      'IMAI가 답장하지 못했습니다. 다시 시도하려면 메시지를 한 번 더 보내 주세요.',
    conversation: 'IMAI와의 대화',
    loadEarlier: '이전 메시지 불러오기',
    unread: '새 메시지 {count}개',
    you: '나',
    imaiName: 'IMAI',
    threadWorking: '작업 중',
    threadNeedsYou: '확인 필요',
    threadStopped: '중단됨',
    threadDidntFinish: '완료 못 함',
    threadDone: '완료',
    openThread: '{title} 열기',
    messageFrom: '{name}의 메시지',
    questionFrom: '{title}에서',
    sentToTask: '작업에 보냄',
    retryTaskPrompt: '“{title}” 다시 시도해 줘',
    react: '반응',
    reply: '답장',
    reactionLove: '하트',
    reactionLike: '좋아요',
    reactionDislike: '싫어요',
    reactionLaugh: '하하',
    reactionWow: '놀라워요',
    reactionSad: '슬퍼요',
    youReacted: '내 반응: {reaction}',
    imaiReacted: 'IMAI의 반응: {reaction}',
    replyingTo: '{name}에게 답장 중',
    replyingToSelf: '나에게 답장 중',
    cancelReply: '답장 취소',
    showQuoted: '인용된 메시지 보기: {text}',
    photo: '사진',
    photoUnavailable: '이미지를 표시할 수 없음',
    newMessage: '새 메시지',
    notifyPrompt: '{name}의 메시지가 오면 알림 받기',
    notifyTurnOn: '켜기',
    notifyDismiss: '나중에',
    latestUpdate: '최근 업데이트',
    glimpseEmpty: '아직 표시할 내용이 없어요.',
    goToChat: '채팅으로 이동',
    outputsLabel: '완료된 작업의 파일',
    stickerGenerating: '스티커 만드는 중',
    stickerLabel: '스티커: {label}',
  },
  pt: {
    navLabel: 'Seu IMAI',
    greeting: 'Oi! Eu sou o seu IMAI.',
    greetingNamed: 'Oi! Eu sou {name}.',
    greetingBody:
      'Me mande mensagem quando quiser. Vou seguir com o trabalho mesmo quando não estivermos conversando e aviso você com novidades ou perguntas.',
    greetingAskName: 'Quer me dar um nome?',
    customize: 'Personalize seu IMAI',
    customizeNamed: 'Personalize {name}',
    colors: 'Cores',
    shapes: 'Formas',
    faces: 'Rostos',
    name: 'Nome',
    save: 'Salvar',
    close: 'Fechar',
    previous: 'Anterior',
    next: 'Próximo',
    saveFailed: 'Não foi possível salvar. Tente de novo.',
    thinking: 'Pensando',
    readAt: 'Lida {time}',
    opening: 'Abrindo seu chat…',
    openFailed: 'Não foi possível abrir Seu IMAI.',
    retry: 'Tentar novamente',
    placeholder: 'Mande uma mensagem para {name}',
    send: 'Enviar',
    delivered: 'Entregue',
    read: 'Lida',
    notDelivered: 'Não entregue',
    retrySend: 'Tentar de novo',
    typing: 'IMAI está digitando',
    replyFailed:
      'IMAI não conseguiu responder. Envie outra mensagem para tentar de novo.',
    conversation: 'Conversa com IMAI',
    loadEarlier: 'Carregar mensagens anteriores',
    unread: '{count} novas',
    you: 'Você',
    imaiName: 'IMAI',
    threadWorking: 'Trabalhando',
    threadNeedsYou: 'Precisa de você',
    threadStopped: 'Parado',
    threadDidntFinish: 'Não terminou',
    threadDone: 'Pronto',
    openThread: 'Abrir {title}',
    messageFrom: 'Mensagem de {name}',
    questionFrom: 'De {title}',
    sentToTask: 'Enviado para a tarefa',
    retryTaskPrompt: 'Tente “{title}” de novo',
    react: 'Reagir',
    reply: 'Responder',
    reactionLove: 'Amei',
    reactionLike: 'Curti',
    reactionDislike: 'Não curti',
    reactionLaugh: 'Haha',
    reactionWow: 'Uau',
    reactionSad: 'Triste',
    youReacted: 'Você reagiu com {reaction}',
    imaiReacted: 'IMAI reagiu com {reaction}',
    replyingTo: 'Respondendo a {name}',
    replyingToSelf: 'Respondendo a você mesmo',
    cancelReply: 'Cancelar resposta',
    showQuoted: 'Mostrar mensagem citada: {text}',
    photo: 'Foto',
    photoUnavailable: 'Imagem indisponível',
    newMessage: 'Nova mensagem',
    notifyPrompt: 'Receba uma notificação quando {name} mandar mensagem',
    notifyTurnOn: 'Ativar',
    notifyDismiss: 'Agora não',
    latestUpdate: 'Última atualização',
    glimpseEmpty: 'Nada para mostrar ainda.',
    goToChat: 'Ir para o chat',
    outputsLabel: 'Arquivos do trabalho concluído',
    stickerGenerating: 'Criando uma figurinha',
    stickerLabel: 'Figurinha: {label}',
  },
  ru: {
    navLabel: 'Ваш IMAI',
    greeting: 'Привет! Я твой IMAI.',
    greetingNamed: 'Привет! Я {name}.',
    greetingBody:
      'Пиши в любое время. Я продолжу работу, даже когда мы не переписываемся, и напишу с новостями или вопросами.',
    greetingAskName: 'Хочешь дать мне имя?',
    customize: 'Настроить твоего IMAI',
    customizeNamed: 'Настроить {name}',
    colors: 'Цвета',
    shapes: 'Формы',
    faces: 'Лица',
    name: 'Имя',
    save: 'Сохранить',
    close: 'Закрыть',
    previous: 'Назад',
    next: 'Далее',
    saveFailed: 'Не удалось сохранить. Попробуй ещё раз.',
    thinking: 'Думает',
    readAt: 'Прочитано {time}',
    opening: 'Открываем ваш чат…',
    openFailed: 'Не удалось открыть «Ваш IMAI».',
    retry: 'Попробовать ещё раз',
    placeholder: 'Написать {name}',
    send: 'Отправить',
    delivered: 'Доставлено',
    read: 'Прочитано',
    notDelivered: 'Не доставлено',
    retrySend: 'Повторить',
    typing: 'IMAI печатает',
    replyFailed:
      'IMAI не смог ответить. Отправьте ещё одно сообщение, чтобы попробовать снова.',
    conversation: 'Разговор с IMAI',
    loadEarlier: 'Загрузить предыдущие сообщения',
    unread: 'Новых: {count}',
    you: 'Вы',
    imaiName: 'IMAI',
    threadWorking: 'В работе',
    threadNeedsYou: 'Нужен ты',
    threadStopped: 'Остановлено',
    threadDidntFinish: 'Не завершено',
    threadDone: 'Готово',
    openThread: 'Открыть {title}',
    messageFrom: 'Сообщение от {name}',
    questionFrom: 'Из {title}',
    sentToTask: 'Отправлено в задачу',
    retryTaskPrompt: 'Попробуй «{title}» ещё раз',
    react: 'Реакция',
    reply: 'Ответить',
    reactionLove: 'Обожаю',
    reactionLike: 'Нравится',
    reactionDislike: 'Не нравится',
    reactionLaugh: 'Ха-ха',
    reactionWow: 'Ого',
    reactionSad: 'Грустно',
    youReacted: 'Ваша реакция: {reaction}',
    imaiReacted: 'Реакция IMAI: {reaction}',
    replyingTo: 'Ответ: {name}',
    replyingToSelf: 'Ответ себе',
    cancelReply: 'Отменить ответ',
    showQuoted: 'Показать цитируемое сообщение: {text}',
    photo: 'Фото',
    photoUnavailable: 'Изображение недоступно',
    newMessage: 'Новое сообщение',
    notifyPrompt: 'Получайте уведомления, когда {name} пишет вам',
    notifyTurnOn: 'Включить',
    notifyDismiss: 'Не сейчас',
    latestUpdate: 'Последнее обновление',
    glimpseEmpty: 'Пока здесь пусто.',
    goToChat: 'Перейти в чат',
    outputsLabel: 'Файлы из завершённой работы',
    stickerGenerating: 'Создаём стикер',
    stickerLabel: 'Стикер: {label}',
  },
  vi: {
    navLabel: 'IMAI của bạn',
    greeting: 'Chào bạn! Mình là IMAI của bạn.',
    greetingNamed: 'Chào bạn! Mình là {name}.',
    greetingBody:
      'Nhắn cho mình bất cứ lúc nào. Mình sẽ tiếp tục xử lý công việc ngay cả khi chúng ta không trò chuyện, và báo cho bạn khi có cập nhật hoặc câu hỏi.',
    greetingAskName: 'Bạn muốn đặt tên cho mình không?',
    customize: 'Tùy chỉnh IMAI của bạn',
    customizeNamed: 'Tùy chỉnh {name}',
    colors: 'Màu sắc',
    shapes: 'Hình dạng',
    faces: 'Khuôn mặt',
    name: 'Tên',
    save: 'Lưu',
    close: 'Đóng',
    previous: 'Trước',
    next: 'Tiếp',
    saveFailed: 'Không lưu được. Hãy thử lại.',
    thinking: 'Đang suy nghĩ',
    readAt: 'Đã xem {time}',
    opening: 'Đang mở cuộc trò chuyện của bạn…',
    openFailed: 'Không thể mở IMAI của bạn.',
    retry: 'Thử lại',
    placeholder: 'Nhắn cho {name}',
    send: 'Gửi',
    delivered: 'Đã gửi',
    read: 'Đã xem',
    notDelivered: 'Chưa gửi được',
    retrySend: 'Thử lại',
    typing: 'IMAI đang nhập',
    replyFailed:
      'IMAI không thể trả lời. Hãy gửi thêm một tin nhắn để thử lại.',
    conversation: 'Cuộc trò chuyện với IMAI',
    loadEarlier: 'Tải tin nhắn trước đó',
    unread: '{count} tin mới',
    you: 'Bạn',
    imaiName: 'IMAI',
    threadWorking: 'Đang làm',
    threadNeedsYou: 'Cần bạn',
    threadStopped: 'Đã dừng',
    threadDidntFinish: 'Chưa xong',
    threadDone: 'Xong',
    openThread: 'Mở {title}',
    messageFrom: 'Tin nhắn từ {name}',
    questionFrom: 'Từ {title}',
    sentToTask: 'Đã gửi cho tác vụ',
    retryTaskPrompt: 'Thử lại “{title}”',
    react: 'Bày tỏ cảm xúc',
    reply: 'Trả lời',
    reactionLove: 'Yêu thích',
    reactionLike: 'Thích',
    reactionDislike: 'Không thích',
    reactionLaugh: 'Haha',
    reactionWow: 'Wow',
    reactionSad: 'Buồn',
    youReacted: 'Bạn đã bày tỏ {reaction}',
    imaiReacted: 'IMAI đã bày tỏ {reaction}',
    replyingTo: 'Đang trả lời {name}',
    replyingToSelf: 'Đang trả lời chính bạn',
    cancelReply: 'Hủy trả lời',
    showQuoted: 'Hiện tin nhắn được trích dẫn: {text}',
    photo: 'Ảnh',
    photoUnavailable: 'Hình ảnh không khả dụng',
    newMessage: 'Tin nhắn mới',
    notifyPrompt: 'Nhận thông báo khi {name} nhắn cho bạn',
    notifyTurnOn: 'Bật',
    notifyDismiss: 'Để sau',
    latestUpdate: 'Cập nhật mới nhất',
    glimpseEmpty: 'Chưa có gì để hiển thị.',
    goToChat: 'Đi đến cuộc trò chuyện',
    outputsLabel: 'Tệp từ công việc đã xong',
    stickerGenerating: 'Đang tạo nhãn dán',
    stickerLabel: 'Nhãn dán: {label}',
  },
  zh: {
    navLabel: '你的 IMAI',
    greeting: '嗨！我是你的 IMAI。',
    greetingNamed: '嗨！我是 {name}。',
    greetingBody:
      '随时给我发消息。即使我们没在聊天，我也会继续推进工作，有进展或问题时会来找你。',
    greetingAskName: '想给我起个名字吗？',
    customize: '自定义你的 IMAI',
    customizeNamed: '自定义 {name}',
    colors: '颜色',
    shapes: '形状',
    faces: '表情',
    name: '名字',
    save: '保存',
    close: '关闭',
    previous: '上一个',
    next: '下一个',
    saveFailed: '保存失败，请重试。',
    thinking: '思考中',
    readAt: '已读 {time}',
    opening: '正在打开你的聊天…',
    openFailed: '无法打开你的 IMAI。',
    retry: '重试',
    placeholder: '给 {name} 发消息',
    send: '发送',
    delivered: '已送达',
    read: '已读',
    notDelivered: '未送达',
    retrySend: '重试',
    typing: 'IMAI 正在输入',
    replyFailed: 'IMAI 无法回复。再发一条消息即可重试。',
    conversation: '与 IMAI 的对话',
    loadEarlier: '加载更早的消息',
    unread: '{count} 条新消息',
    you: '你',
    imaiName: 'IMAI',
    threadWorking: '进行中',
    threadNeedsYou: '需要你',
    threadStopped: '已停止',
    threadDidntFinish: '未完成',
    threadDone: '完成',
    openThread: '打开 {title}',
    messageFrom: '来自 {name} 的消息',
    questionFrom: '来自 {title}',
    sentToTask: '已发送给任务',
    retryTaskPrompt: '重试“{title}”',
    react: '回应',
    reply: '回复',
    reactionLove: '喜爱',
    reactionLike: '赞',
    reactionDislike: '踩',
    reactionLaugh: '哈哈',
    reactionWow: '哇',
    reactionSad: '难过',
    youReacted: '你回应了 {reaction}',
    imaiReacted: 'IMAI 回应了 {reaction}',
    replyingTo: '回复 {name}',
    replyingToSelf: '回复自己',
    cancelReply: '取消回复',
    showQuoted: '显示引用的消息：{text}',
    photo: '照片',
    photoUnavailable: '图片不可用',
    newMessage: '新消息',
    notifyPrompt: '{name} 给你发消息时接收通知',
    notifyTurnOn: '开启',
    notifyDismiss: '以后再说',
    latestUpdate: '最新进展',
    glimpseEmpty: '暂时没有内容。',
    goToChat: '前往聊天',
    outputsLabel: '已完成工作的文件',
    stickerGenerating: '正在生成贴纸',
    stickerLabel: '贴纸：{label}',
  },
  'zh-tw': {
    navLabel: '你的 IMAI',
    greeting: '嗨！我是你的 IMAI。',
    greetingNamed: '嗨！我是 {name}。',
    greetingBody:
      '隨時傳訊息給我。即使我們沒在聊天，我也會繼續推進工作，有進度或問題時會來找你。',
    greetingAskName: '想幫我取個名字嗎？',
    customize: '自訂你的 IMAI',
    customizeNamed: '自訂 {name}',
    colors: '顏色',
    shapes: '形狀',
    faces: '表情',
    name: '名字',
    save: '儲存',
    close: '關閉',
    previous: '上一個',
    next: '下一個',
    saveFailed: '儲存失敗，請再試一次。',
    thinking: '思考中',
    readAt: '已讀 {time}',
    opening: '正在開啟你的聊天…',
    openFailed: '無法開啟你的 IMAI。',
    retry: '重試',
    placeholder: '傳訊息給 {name}',
    send: '傳送',
    delivered: '已送達',
    read: '已讀',
    notDelivered: '未送達',
    retrySend: '重試',
    typing: 'IMAI 正在輸入',
    replyFailed: 'IMAI 無法回覆。再傳一則訊息即可重試。',
    conversation: '與 IMAI 的對話',
    loadEarlier: '載入較早的訊息',
    unread: '{count} 則新訊息',
    you: '你',
    imaiName: 'IMAI',
    threadWorking: '進行中',
    threadNeedsYou: '需要你',
    threadStopped: '已停止',
    threadDidntFinish: '未完成',
    threadDone: '完成',
    openThread: '開啟 {title}',
    messageFrom: '來自 {name} 的訊息',
    questionFrom: '來自 {title}',
    sentToTask: '已傳送給任務',
    retryTaskPrompt: '重試「{title}」',
    react: '回應',
    reply: '回覆',
    reactionLove: '喜愛',
    reactionLike: '讚',
    reactionDislike: '倒讚',
    reactionLaugh: '哈哈',
    reactionWow: '哇',
    reactionSad: '難過',
    youReacted: '你回應了 {reaction}',
    imaiReacted: 'IMAI 回應了 {reaction}',
    replyingTo: '回覆 {name}',
    replyingToSelf: '回覆自己',
    cancelReply: '取消回覆',
    showQuoted: '顯示引用的訊息：{text}',
    photo: '照片',
    photoUnavailable: '圖片無法使用',
    newMessage: '新訊息',
    notifyPrompt: '{name} 傳訊息給你時接收通知',
    notifyTurnOn: '開啟',
    notifyDismiss: '以後再說',
    latestUpdate: '最新進度',
    glimpseEmpty: '目前還沒有內容。',
    goToChat: '前往聊天',
    outputsLabel: '已完成工作的檔案',
    stickerGenerating: '正在製作貼圖',
    stickerLabel: '貼圖：{label}',
  },
};

export function getYourImaiCopy(locale: string): YourImaiCopy {
  const normalizedLocale = locale.toLowerCase();
  const language = normalizedLocale.split('-')[0] ?? 'en';
  return (
    COPY_BY_LANGUAGE[normalizedLocale] ??
    COPY_BY_LANGUAGE[language] ??
    DEFAULT_COPY
  );
}
